import L from "leaflet";

export const contoursTool = {
  id: "contours",
  name: "Contours (ETOPO)",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
};

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;
  const root = L.featureGroup().addTo(map);
  const drawLayer = L.featureGroup().addTo(root);
  const contourLayer = L.featureGroup().addTo(root);

  let container = null;
  let aoiPoly = null;
  let drawSession = null;
  let running = false;
  let runId = 0;
  let globalLayerId = null;
  let inGlobalSync = false;
  let visible = true;
  let opacity = 1;
  let statusText = "idle";
  let contourCount = 0;

  const state = {
    gridM: 100,
    intervalM: 10,
    skipLand: true,
    color: "#22c55e",
    smooth: true,
  };

  function esc(v) {
    return String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
  }

  function setStatus(msg) {
    statusText = String(msg || "");
    const el = container?.querySelector?.("#ctr-status");
    if (el) el.textContent = statusText;
  }

  function setCount(n) {
    contourCount = Math.max(0, Number(n) || 0);
    const el = container?.querySelector?.("#ctr-count");
    if (el) el.textContent = `${contourCount} contours`;
  }

  function setProgress(fraction, text = "") {
    const bar = container?.querySelector?.("#contour-progress-bar");
    const label = container?.querySelector?.("#contour-progress-label");
    if (!bar || !label) return;
    const f = Math.max(0, Math.min(1, Number(fraction) || 0));
    bar.style.width = `${(f * 100).toFixed(1)}%`;
    label.textContent = text || (f === 0 ? "idle" : `${Math.round(f * 100)}%`);
  }

  function resetProgress() {
    setProgress(0, "idle");
  }

  function applyOpacity(o01) {
    const o = Math.max(0, Math.min(1, Number(o01) || 0));
    opacity = o;
    if (aoiPoly?.setStyle) {
      try { aoiPoly.setStyle({ opacity: Math.max(0.1, o), fillOpacity: Math.max(0.02, 0.1 * o) }); } catch {}
    }
    contourLayer.eachLayer((l) => {
      if (l?.setStyle) {
        try { l.setStyle({ opacity: Math.max(0.1, 0.9 * o) }); } catch {}
      } else if (l?.setOpacity) {
        try { l.setOpacity(o); } catch {}
      }
    });
  }

  function syncGlobalLayer() {
    if (!globalLayerId || inGlobalSync) return;
    app.services.layers.updateLayer(globalLayerId, {
      name: "Contours (ETOPO)",
      visible,
      opacity,
      group: "Contours",
      meta: { featureCount: contourCount, hasAOI: !!aoiPoly },
    });
  }

  function ensureGlobalLayer() {
    if (globalLayerId) return;
    globalLayerId = app.services.layers.addLayer("contours", {
      name: "Contours (ETOPO)",
      kind: "overlay",
      group: "Contours",
      type: "managed",
      visible,
      opacity,
      volatile: true,
      meta: { featureCount: contourCount, hasAOI: !!aoiPoly },
      managed: {
        setVisible(v) {
          visible = !!v;
          if (visible) root.addTo(map);
          else map.removeLayer(root);
          if (!inGlobalSync) render();
        },
        setOpacity(v) {
          applyOpacity(v);
          if (!inGlobalSync) render();
        },
        getBounds() {
          const b = root.getBounds?.();
          return b?.isValid?.() ? b : null;
        },
        bringToFront() {
          try { root.bringToFront?.(); } catch {}
        },
        remove() {
          if (inGlobalSync) return;
          inGlobalSync = true;
          try {
            clearAll();
            visible = false;
            map.removeLayer(root);
            globalLayerId = null;
          } finally {
            inGlobalSync = false;
          }
          render();
        },
      },
    });
  }

  function metersToDegrees(lat, meters) {
    const dLat = meters / 111320;
    const dLon = meters / (111320 * Math.cos((lat * Math.PI) / 180));
    return { dLat, dLon };
  }

  function pointInPoly(p, ring) {
    let inside = false;
    const x = p.lng;
    const y = p.lat;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const xi = ring[i].lng;
      const yi = ring[i].lat;
      const xj = ring[j].lng;
      const yj = ring[j].lat;
      const inter = ((yi > y) !== (yj > y)) && (x < (((xj - xi) * (y - yi)) / (yj - yi)) + xi);
      if (inter) inside = !inside;
    }
    return inside;
  }

  function smoothGrid(z) {
    const rows = z.length;
    const cols = z[0].length;
    const out = Array.from({ length: rows }, () => Array(cols).fill(NaN));
    for (let i = 0; i < rows; i += 1) {
      for (let j = 0; j < cols; j += 1) {
        let sum = 0;
        let n = 0;
        for (let di = -1; di <= 1; di += 1) {
          for (let dj = -1; dj <= 1; dj += 1) {
            const ii = i + di;
            const jj = j + dj;
            if (ii < 0 || jj < 0 || ii >= rows || jj >= cols) continue;
            const v = z[ii][jj];
            if (!Number.isFinite(v)) continue;
            sum += v;
            n += 1;
          }
        }
        if (n > 0) out[i][j] = sum / n;
      }
    }
    for (let i = 0; i < rows; i += 1) {
      for (let j = 0; j < cols; j += 1) {
        if (Number.isFinite(out[i][j])) z[i][j] = out[i][j];
      }
    }
  }

  function buildContourSegments(z, latVals, lngVals, level) {
    const rows = z.length;
    const cols = z[0].length;
    const segs = [];

    function addCell(i, j) {
      const z00 = z[i][j];
      const z10 = z[i + 1][j];
      const z11 = z[i + 1][j + 1];
      const z01 = z[i][j + 1];
      if (![z00, z10, z11, z01].every((v) => Number.isFinite(v))) return;
      const lat0 = latVals[i];
      const lat1 = latVals[i + 1];
      const lng0 = lngVals[j];
      const lng1 = lngVals[j + 1];
      const pts = [];

      function edge(zA, zB, latA, lonA, latB, lonB) {
        const vA = zA - level;
        const vB = zB - level;
        if (vA === 0 && vB === 0) return;
        if (vA === 0) { pts.push(L.latLng(latA, lonA)); return; }
        if (vB === 0) { pts.push(L.latLng(latB, lonB)); return; }
        if (vA * vB < 0) {
          const t = vA / (vA - vB);
          pts.push(L.latLng(latA + (t * (latB - latA)), lonA + (t * (lonB - lonA))));
        }
      }

      edge(z00, z01, lat0, lng0, lat0, lng1);
      edge(z01, z11, lat0, lng1, lat1, lng1);
      edge(z11, z10, lat1, lng1, lat1, lng0);
      edge(z10, z00, lat1, lng0, lat0, lng0);

      if (pts.length === 2) segs.push([pts[0], pts[1]]);
      else if (pts.length === 4) {
        segs.push([pts[0], pts[1]]);
        segs.push([pts[2], pts[3]]);
      }
    }

    for (let i = 0; i < rows - 1; i += 1) {
      for (let j = 0; j < cols - 1; j += 1) addCell(i, j);
    }
    return segs;
  }

  function readStateFromUi() {
    const n = (id, fallback, min = 0) => {
      const v = Number(container?.querySelector?.(`#${id}`)?.value);
      if (!Number.isFinite(v)) return fallback;
      return Math.max(min, v);
    };
    state.gridM = n("ctr-grid", state.gridM, 1);
    state.intervalM = n("ctr-interval", state.intervalM, 0.1);
    state.color = String(container?.querySelector?.("#ctr-color")?.value || state.color);
    state.skipLand = !!container?.querySelector?.("#ctr-skip-land")?.checked;
    state.smooth = !!container?.querySelector?.("#ctr-smooth")?.checked;
  }

  async function fetchEtopoValue(lat, lng) {
    try {
      const r = await app.services.identify.sampleDepth(lng, lat, "etopo");
      const v = Number(r?.depth_m);
      return Number.isFinite(v) ? v : null;
    } catch {
      return null;
    }
  }

  function stopDraw() {
    if (!drawSession) return;
    drawSession.off?.();
    drawSession.cleanup?.();
    drawSession = null;
    mapEl.style.cursor = "";
  }

  function startDrawAOI() {
    ensureGlobalLayer();
    stopDraw();
    setStatus("Draw AOI: click to add vertices, double-click to finish");
    drawLayer.clearLayers();
    if (aoiPoly) {
      try { root.removeLayer(aoiPoly); } catch {}
      aoiPoly = null;
    }
    const verts = [];
    const tmp = L.polygon([], { color: "#f97316", weight: 2, opacity: Math.max(0.2, opacity), fillOpacity: 0.1, dashArray: "6 4" }).addTo(drawLayer);
    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();
    mapEl.style.cursor = "crosshair";

    const onClick = (e) => {
      verts.push(e.latlng);
      tmp.setLatLngs(verts);
    };
    const onDbl = (e) => {
      L.DomEvent.stop(e);
      if (verts.length < 3) {
        setStatus("AOI needs >= 3 vertices");
        return;
      }
      drawLayer.clearLayers();
      aoiPoly = L.polygon(verts, { color: "#f97316", weight: 2, opacity: Math.max(0.2, opacity), fillOpacity: Math.max(0.02, 0.1 * opacity) }).addTo(root);
      setStatus("AOI set");
      syncGlobalLayer();
      render();
      stopDraw();
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setStatus("AOI draw cancelled");
      stopDraw();
    };
    drawSession = {
      off() {
        map.off("click", onClick);
        map.off("dblclick", onDbl);
        document.removeEventListener("keydown", onKey);
        if (dblWas) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      },
      cleanup() {
        try { drawLayer.removeLayer(tmp); } catch {}
      },
    };
    map.on("click", onClick);
    map.on("dblclick", onDbl);
    document.addEventListener("keydown", onKey);
  }

  async function generateContours() {
    if (!aoiPoly) {
      setStatus("Draw AOI first");
      return;
    }
    readStateFromUi();
    const ring = aoiPoly.getLatLngs?.()[0] || aoiPoly.getLatLngs?.();
    if (!Array.isArray(ring) || ring.length < 3) {
      setStatus("AOI invalid");
      return;
    }
    const lats = ring.map((p) => p.lat);
    const lngs = ring.map((p) => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const midLat = (minLat + maxLat) / 2;
    const step = metersToDegrees(midLat, state.gridM);
    if (!(step.dLat > 0 && step.dLon > 0)) {
      setStatus("Grid spacing invalid");
      return;
    }
    const latVals = [];
    const lngVals = [];
    for (let lat = minLat; lat <= maxLat + 1e-9; lat += step.dLat) latVals.push(lat);
    for (let lng = minLng; lng <= maxLng + 1e-9; lng += step.dLon) lngVals.push(lng);
    const nRows = latVals.length;
    const nCols = lngVals.length;
    const total = nRows * nCols;
    const MAX_SAMPLES = 12000;
    if (total > MAX_SAMPLES) {
      setStatus(`AOI too dense (${total} samples). Increase grid spacing.`);
      return;
    }

    running = true;
    const myRun = ++runId;
    resetProgress();
    setStatus("Sampling ETOPO...");
    const z = Array.from({ length: nRows }, () => Array(nCols).fill(NaN));
    const depths = [];
    for (let i = 0; i < nRows; i += 1) {
      if (!running || myRun !== runId) {
        setStatus("Cancelled");
        return;
      }
      const lat = latVals[i];
      for (let j = 0; j < nCols; j += 1) {
        const lng = lngVals[j];
        if (!pointInPoly({ lat, lng }, ring)) continue;
        const val = await fetchEtopoValue(lat, lng);
        if (!Number.isFinite(val)) continue;
        if (state.skipLand && val >= 0) continue;
        z[i][j] = val;
        if (val < 0) depths.push(-val);
      }
      setProgress((i + 1) / nRows);
      if (i % 3 === 0) await new Promise((r) => setTimeout(r, 0));
    }
    setProgress(1, "done");
    if (!depths.length) {
      contourLayer.clearLayers();
      setCount(0);
      setStatus("No underwater ETOPO cells in AOI.");
      syncGlobalLayer();
      return;
    }

    const minDepth = Math.min(...depths);
    const maxDepth = Math.max(...depths);
    const start = Math.ceil(minDepth / state.intervalM) * state.intervalM;
    const end = Math.floor(maxDepth / state.intervalM) * state.intervalM;
    const levels = [];
    for (let d = start; d <= end + 1e-9; d += state.intervalM) levels.push(-d);
    if (!levels.length) {
      setStatus("No contour levels for interval in this AOI.");
      setCount(0);
      return;
    }
    if (state.smooth) smoothGrid(z);

    contourLayer.clearLayers();
    let built = 0;
    levels.forEach((level, idx) => {
      const segs = buildContourSegments(z, latVals, lngVals, level);
      segs.forEach((seg) => {
        L.polyline(seg, { color: state.color, weight: 1.5, opacity: Math.max(0.1, 0.9 * opacity) }).addTo(contourLayer);
        built += 1;
      });
      if (idx % 2 === 0) setStatus(`Building contours: depth ${Math.abs(level).toFixed(1)} m`);
    });
    setCount(built);
    setStatus(`Done (${built} contour segments)`);
    syncGlobalLayer();
  }

  function clearAll() {
    running = false;
    runId += 1;
    stopDraw();
    drawLayer.clearLayers();
    contourLayer.clearLayers();
    if (aoiPoly) {
      try { root.removeLayer(aoiPoly); } catch {}
      aoiPoly = null;
    }
    setCount(0);
    setStatus("cleared");
    resetProgress();
    syncGlobalLayer();
  }

  function render() {
    if (!container) return;
    container.innerHTML = `
      <div class="card">
        <div style="font-weight:700;">Contours (ETOPO)</div>
        <div class="small" style="margin-top:6px;">Draw an AOI, choose spacing/interval, then generate depth contours.</div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap;">
          <button id="ctr-draw" class="btn primary">Draw AOI</button>
          <button id="ctr-generate" class="btn">Generate contours</button>
          <button id="ctr-clear" class="btn">Clear</button>
          <span id="ctr-status" class="muted" style="margin-left:auto;">${esc(statusText)}</span>
        </div>
        <div id="contour-progress-wrap" class="muted" style="margin-top:6px;">
          <span id="contour-progress-label">idle</span>
          <div style="height:6px; background:rgba(255,255,255,0.08); border-radius:999px; overflow:hidden; margin-top:4px;">
            <div id="contour-progress-bar" style="height:100%; width:0%; background:#22c55e; transition:width 0.18s linear;"></div>
          </div>
        </div>
        <div class="row" style="margin-top:8px; gap:12px; flex-wrap:wrap;">
          <label class="small">Grid spacing<br><input id="ctr-grid" type="number" value="${state.gridM}" min="10" step="10" style="width:90px;"> m</label>
          <label class="small">Contour interval<br><input id="ctr-interval" type="number" value="${state.intervalM}" min="1" step="1" style="width:90px;"> m</label>
          <label class="small">Line color<br><input id="ctr-color" type="color" value="${esc(state.color)}"></label>
        </div>
        <div class="row" style="margin-top:8px; gap:12px; flex-wrap:wrap;">
          <label class="small"><input id="ctr-skip-land" type="checkbox" ${state.skipLand ? "checked" : ""}> Skip land</label>
          <label class="small"><input id="ctr-smooth" type="checkbox" ${state.smooth ? "checked" : ""}> Smooth</label>
          <span id="ctr-count" class="muted" style="margin-left:auto;">${contourCount} contours</span>
        </div>
      </div>
    `;
    resetProgress();
    setCount(contourCount);
    setStatus(statusText);
  }

  function onClick(e) {
    const id = e.target?.id;
    if (!id) return;
    if (id === "ctr-draw") { startDrawAOI(); return; }
    if (id === "ctr-generate") {
      if (running) return;
      ensureGlobalLayer();
      generateContours().finally(() => { running = false; });
      return;
    }
    if (id === "ctr-clear") { clearAll(); render(); return; }
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    render();
    container.addEventListener("click", onClick);
    syncGlobalLayer();
  }

  function snapshot() {
    return {
      state: { ...state },
      statusText,
      contourCount,
      aoi: aoiPoly ? (aoiPoly.getLatLngs?.()[0] || []).map((ll) => [ll.lat, ll.lng]) : null,
    };
  }

  function restore(data) {
    clearAll();
    if (!data || typeof data !== "object") return;
    if (data.state && typeof data.state === "object") {
      state.gridM = Math.max(1, Number(data.state.gridM) || state.gridM);
      state.intervalM = Math.max(0.1, Number(data.state.intervalM) || state.intervalM);
      state.skipLand = data.state.skipLand !== false;
      state.color = typeof data.state.color === "string" ? data.state.color : state.color;
      state.smooth = data.state.smooth !== false;
    }
    statusText = typeof data.statusText === "string" ? data.statusText : "idle";
    contourCount = Math.max(0, Number(data.contourCount) || 0);
    const ring = Array.isArray(data.aoi) ? data.aoi : null;
    if (ring && ring.length >= 3) {
      ensureGlobalLayer();
      aoiPoly = L.polygon(ring.map(([lat, lng]) => L.latLng(Number(lat), Number(lng))), {
        color: "#f97316",
        weight: 2,
        opacity: Math.max(0.2, opacity),
        fillOpacity: Math.max(0.02, 0.1 * opacity),
      }).addTo(root);
    }
    if (contourCount > 0 && !globalLayerId) ensureGlobalLayer();
    render();
    syncGlobalLayer();
  }

  function destroy() {
    running = false;
    runId += 1;
    stopDraw();
    if (container) container.removeEventListener("click", onClick);
    clearAll();
    if (globalLayerId) {
      inGlobalSync = true;
      try { app.services.layers.removeLayer(globalLayerId); } catch {}
      finally { inGlobalSync = false; }
      globalLayerId = null;
    }
    try { map.removeLayer(root); } catch {}
    container = null;
  }

  return { mount, snapshot, restore, destroy };
}
